import React, { useCallback, useEffect, useState } from 'react';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} aria-hidden="true" />;

/**
 * LE JOURNAL D'ACTIVITÉ — qui a fait quoi, et quand (demande 28.09 : « pour
 * savoir qui fait quoi et quand, et laisser une trace — surtout via le chat »).
 *
 * Réservé aux administrateurs (règle serveur : GET /journal-activite). Le
 * serveur calcule la phrase de chaque ligne ; l'écran l'affiche, il ne la
 * recompose pas. L'origine est l'information qui compte le plus ici : un geste
 * fait À L'ÉCRAN, par un LIEN de document, ou PAR LE COPILOTE — c'est elle qui
 * porte la seule marque de l'écran.
 */
const ORIGINES = [
  { cle: '', libelle: 'Toutes' },
  { cle: 'ecran', libelle: 'Écran', icone: 'display' },
  { cle: 'copilote', libelle: 'Copilote', icone: 'chat-square-text' },
  { cle: 'lien', libelle: 'Lien de document', icone: 'link-45deg' },
];
const ICONE_ORIGINE = { ecran: 'display', copilote: 'chat-square-text', lien: 'link-45deg' };
const PERIODES = [
  { cle: 'jour', libelle: "Aujourd'hui", jours: 0 },
  { cle: 'semaine', libelle: '7 jours', jours: 7 },
  { cle: 'mois', libelle: '30 jours', jours: 30 },
  { cle: 'tout', libelle: 'Tout', jours: null },
];
const NATURES = [
  { cle: '', libelle: 'Tout' },
  { cle: 'ecriture', libelle: 'Modifications' },
  { cle: 'fichier', libelle: 'Documents ouverts' },
  { cle: 'lecture', libelle: 'Consultations du copilote' },
  { cle: 'connexion', libelle: 'Connexions' },
];

const debutDe = (periode) => {
  const p = PERIODES.find(x => x.cle === periode);
  if (!p || p.jours == null) return null;
  const d = new Date(); d.setHours(0, 0, 0, 0);
  if (p.jours) d.setDate(d.getDate() - p.jours);
  return d.toISOString();
};
const heure = (iso) => {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}`;
  } catch (e) { return '—'; }
};

export default function JournalActivite({ onOuvrirCandidat, onOuvrirBesoin }) {
  const [lignes, setLignes] = useState(null);
  const [total, setTotal] = useState(0);
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [page, setPage] = useState(1);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [filtres, setFiltres] = useState({ user_id: '', via: '', nature: '', periode: 'semaine', q: '' });
  const [qSaisie, setQSaisie] = useState('');

  // La recherche part 300 ms après la dernière frappe.
  useEffect(() => { const t = setTimeout(() => setFiltres(f => ({ ...f, q: qSaisie.trim() })), 300); return () => clearTimeout(t); }, [qSaisie]);

  const charger = useCallback(async (p = 1) => {
    setChargement(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '50' });
      if (filtres.user_id) params.set('user_id', filtres.user_id);
      if (filtres.via) params.set('via', filtres.via);
      if (filtres.nature) params.set('nature', filtres.nature);
      if (filtres.q) params.set('q', filtres.q);
      const du = debutDe(filtres.periode);
      if (du) params.set('du', du);
      const r = await fetch(`${BASE}/journal-activite?${params.toString()}`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      if (r.status === 404) { setErreur('arrive'); setLignes([]); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setErreur(null);
      setTotal(j.total || 0);
      setUtilisateurs(j.utilisateurs || []);
      setLignes(prev => (p === 1 ? (j.lignes || []) : [...(prev || []), ...(j.lignes || [])]));
      setPage(p);
    } catch (e) {
      setErreur(e.message);
    } finally { setChargement(false); }
  }, [filtres]);

  useEffect(() => { charger(1); }, [charger]);

  const poser = (k, v) => setFiltres(f => ({ ...f, [k]: v }));
  const ouvrirCible = (l) => {
    if (l.cible_type === 'candidat' && l.cible_id && /^\d+$/.test(l.cible_id) && onOuvrirCandidat) return () => onOuvrirCandidat(Number(l.cible_id));
    if (l.cible_type === 'besoin' && l.cible_id && /^\d+$/.test(l.cible_id) && onOuvrirBesoin) return () => onOuvrirBesoin(Number(l.cible_id));
    return null;
  };

  return (
    <div className="ja-page">
      <div className="ja-entete">
        <h1 className="ja-titre">Journal d'activité</h1>
        {lignes && <span className="ja-compte">{total.toLocaleString('fr-FR')} ligne{total > 1 ? 's' : ''}</span>}
      </div>
      <p className="ja-intro">
        Qui a fait quoi, et quand : chaque modification faite depuis l'écran, chaque document ouvert, et chaque action ou consultation
        faite <b>par le copilote</b>. Réservé aux administrateurs ; rien n'y est modifiable.
      </p>

      <div className="ja-filtres">
        <div className="ja-segment" role="group" aria-label="Origine">
          {ORIGINES.map(o => (
            <button key={o.cle || 'toutes'} type="button" aria-pressed={filtres.via === o.cle}
              className={`ja-segment-bt${filtres.via === o.cle ? ' ja-segment-bt--on' : ''}`} onClick={() => poser('via', o.cle)}>
              {o.icone && <BI icon={o.icone} />} {o.libelle}
            </button>
          ))}
        </div>
        <div className="ja-segment" role="group" aria-label="Période">
          {PERIODES.map(p => (
            <button key={p.cle} type="button" aria-pressed={filtres.periode === p.cle}
              className={`ja-segment-bt${filtres.periode === p.cle ? ' ja-segment-bt--on' : ''}`} onClick={() => poser('periode', p.cle)}>
              {p.libelle}
            </button>
          ))}
        </div>
        <select className="erp-field ja-select" value={filtres.user_id} onChange={e => poser('user_id', e.target.value)} aria-label="Personne">
          <option value="">Tout le monde</option>
          {utilisateurs.map(u => <option key={u.id} value={u.id}>{u.nom || `Utilisateur ${u.id}`}</option>)}
        </select>
        <select className="erp-field ja-select" value={filtres.nature} onChange={e => poser('nature', e.target.value)} aria-label="Nature">
          {NATURES.map(n => <option key={n.cle || 'tout'} value={n.cle}>{n.libelle}</option>)}
        </select>
        <input className="erp-field ja-recherche" value={qSaisie} onChange={e => setQSaisie(e.target.value)}
          placeholder="Chercher une action, un nom…" aria-label="Chercher dans le journal" />
      </div>

      {erreur === 'arrive' && <div className="erp-card ja-note">Le journal d'activité arrive côté serveur : il se remplira dès sa mise en ligne.</div>}
      {erreur && erreur !== 'arrive' && <div className="erp-card ja-note ja-note--erreur">{erreur}</div>}

      {lignes && lignes.length === 0 && !erreur && (
        <div className="erp-card ja-note">
          {filtres.q || filtres.user_id || filtres.via || filtres.nature
            ? 'Rien ne correspond à ces filtres sur cette période.'
            : "Rien n'a été relevé sur cette période. Le journal commence le jour de sa mise en ligne : les gestes d'avant n'y figurent pas."}
        </div>
      )}

      {lignes && lignes.length > 0 && (
        <div className="ja-liste">
          {lignes.map(l => {
            const ouvrir = ouvrirCible(l);
            return (
              <div key={l.id} className={`ja-ligne${l.via === 'copilote' ? ' ja-ligne--copilote' : ''}`}>
                <span className="ja-quand">{heure(l.created_at)}</span>
                <span className="ja-qui">{l.user_nom || '—'}</span>
                <div className="ja-quoi">
                  <span>{l.libelle || l.action}</span>
                  {/* La phrase du copilote dit déjà « refusé : raison » : on ne le répète pas. */}
                  {l.reussi === false && !/refusé/.test(l.libelle || '') && <span className="ja-echec"><BI icon="x-circle" /> refusé{l.statut ? ` (${l.statut})` : ''}</span>}
                  {ouvrir && <button type="button" className="ja-lien" onClick={ouvrir}>Ouvrir</button>}
                </div>
                <span className={`ja-via ja-via--${l.via}`} title={l.nature_libelle}>
                  <BI icon={ICONE_ORIGINE[l.via] || (l.nature === 'connexion' ? 'box-arrow-in-right' : 'dot')} /> {l.via_libelle}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {lignes && lignes.length < total && (
        <div className="ja-suite">
          <button type="button" className="erp-btn erp-btn--secondary" disabled={chargement} onClick={() => charger(page + 1)}>
            <BI icon={chargement ? 'arrow-repeat' : 'chevron-down'} /> {chargement ? 'Chargement…' : 'Plus ancien'}
          </button>
        </div>
      )}
    </div>
  );
}
